import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { PageIntro } from "../components/PageIntro.js";
import { Icon } from "../components/Icon.js";
import { previewFamilyPlanImport } from "../family/backup.js";
import { useI18n } from "../i18n/I18nProvider.js";
import { useContentStatus } from "../offline/ContentStatusProvider.js";
import {
  fictionalDrillIds,
  type FictionalDrillId,
  eraseDrillCompletion,
  loadDrillCompletion,
  toggleDrillCompletion,
} from "./drills.js";
import { loadReadinessSnapshot, type ReadinessSnapshot } from "./readiness.js";

export function ReadinessPage() {
  const { locale, t } = useI18n();
  const { refresh: refreshContent } = useContentStatus();
  const [snapshot, setSnapshot] = useState<ReadinessSnapshot>();
  const [completed, setCompleted] = useState<readonly FictionalDrillId[]>([]);
  const [backupText, setBackupText] = useState("");
  const [backupPassphrase, setBackupPassphrase] = useState("");
  const [backupResult, setBackupResult] = useState<"IDLE" | "VALID" | "INVALID">("IDLE");
  const heading = useRef<HTMLHeadingElement>(null);

  const refresh = async () => {
    await refreshContent();
    const [next, drills] = await Promise.all([
      loadReadinessSnapshot(locale),
      loadDrillCompletion(),
    ]);
    setSnapshot(next);
    setCompleted(drills.completedIds);
  };

  useEffect(() => {
    void refresh();
  }, [locale]);

  const verifyBackup = async () => {
    const result = await previewFamilyPlanImport(backupText, backupPassphrase);
    setBackupPassphrase("");
    setBackupResult(result.ok ? "VALID" : "INVALID");
  };

  const restartDrills = async () => {
    await eraseDrillCompletion();
    setCompleted([]);
    heading.current?.focus();
  };

  const printChecklist = () => window.print();

  return (
    <div className="page-stack readiness-page">
      <PageIntro
        body={t("readiness.body")}
        eyebrow={t("readiness.eyebrow")}
        title={t("readiness.title")}
      />
      <section className="product-draft-note" aria-labelledby="readiness-draft-title">
        <p className="eyebrow">{t("draft.status.RESEARCH_ONLY")}</p>
        <h2 id="readiness-draft-title">{t("readiness.draft.title")}</h2>
        <p>{t("readiness.draft.explanation")}</p>
        <dl>
          <div>
            <dt>{t("draft.scope")}</dt>
            <dd>{t("readiness.draft.scope")}</dd>
          </div>
          <div>
            <dt>{t("draft.source")}</dt>
            <dd>
              <Link to="/settings">{t("readiness.draft.source")}</Link>
            </dd>
          </div>
          <div>
            <dt>{t("draft.researched")}</dt>
            <dd>2026-10-01</dd>
          </div>
          <div>
            <dt>{t("draft.limitations")}</dt>
            <dd>{t("readiness.draft.limitations")}</dd>
          </div>
        </dl>
      </section>
      <section
        aria-labelledby="readiness-signals"
        className="readiness-section readiness-section--signals"
      >
        <div className="readiness-heading">
          <h2 id="readiness-signals" ref={heading} tabIndex={-1}>
            {t("readiness.signals")}
          </h2>
          <button className="button" onClick={() => void refresh()}>
            {t("readiness.refresh")}
          </button>
        </div>
        <p className="fixture-note">{t("readiness.fixture")}</p>
        <div className="readiness-grid">
          {snapshot?.signals.map((signal) => (
            <article
              className={`readiness-signal readiness-signal--${signal.state.toLowerCase()}`}
              key={signal.id}
            >
              <span className="readiness-signal__icon">
                <Icon
                  name={
                    signal.state === "READY"
                      ? "check"
                      : signal.state === "OPTIONAL"
                        ? "shield"
                        : "alert"
                  }
                />
              </span>
              <h3>{t(`readiness.signal.${signal.id}`)}</h3>
              <p>{t(`readiness.state.${signal.state}`)}</p>
            </article>
          )) ?? <p>{t("readiness.loading")}</p>}
        </div>
      </section>
      <section className="readiness-backup readiness-section" aria-labelledby="readiness-backup">
        <h2 id="readiness-backup">{t("readiness.backup.title")}</h2>
        <p>
          {snapshot?.backupExportedAt
            ? `${t("readiness.backup.present")}: ${snapshot.backupExportedAt}`
            : t("readiness.backup.none")}
        </p>
        <p>{t("readiness.backup.privacy")}</p>
        <label>
          {t("readiness.backup.data")}
          <textarea onChange={(event) => setBackupText(event.target.value)} value={backupText} />
        </label>
        <label>
          {t("readiness.backup.passphrase")}
          <input
            onChange={(event) => setBackupPassphrase(event.target.value)}
            type="password"
            value={backupPassphrase}
          />
        </label>
        <button
          className="button"
          disabled={!backupText || !backupPassphrase}
          onClick={() => void verifyBackup()}
        >
          {t("readiness.backup.verify")}
        </button>
        {backupResult !== "IDLE" && (
          <p aria-live="polite">{t(`readiness.backup.${backupResult}`)}</p>
        )}
      </section>
      <section
        aria-labelledby="drill-title"
        className="readiness-section readiness-section--drills"
      >
        <h2 id="drill-title">{t("readiness.drill.title")}</h2>
        <p>{t("readiness.drill.body")}</p>
        <p aria-live="polite">
          {t("readiness.drill.progress")
            .replace("{{completed}}", String(completed.length))
            .replace("{{total}}", String(fictionalDrillIds.length))}
        </p>
        <ol className="drill-list">
          {fictionalDrillIds.map((id, index) => (
            <li key={id}>
              <div>
                <strong>{`${index + 1}. ${t(`readiness.drill.${id}`)}`}</strong>
                <p>{t("readiness.drill.fixture")}</p>
              </div>
              <button
                aria-pressed={completed.includes(id)}
                className="button"
                onClick={() =>
                  void toggleDrillCompletion(id).then((next) => setCompleted(next.completedIds))
                }
              >
                {completed.includes(id) ? t("readiness.drill.complete") : t("readiness.drill.mark")}
              </button>
            </li>
          ))}
        </ol>
        <div className="button-row">
          <button className="button" onClick={() => void restartDrills()}>
            {t("readiness.drill.restart")}
          </button>
          <button className="button" onClick={printChecklist}>
            {t("readiness.print")}
          </button>
          <Link className="button" to="/">
            {t("readiness.drill.exit")}
          </Link>
        </div>
      </section>
    </div>
  );
}
